import { useState } from "react";
import axios from "axios";
import {
    Box,
    Heading,
    Text,
    FormControl,
    FormLabel,
    Input,
    Switch,
    HStack,
    Button,
    useToast,
    VStack,
    Divider,
    Badge,
} from "@chakra-ui/react";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";


const CARS_API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/v1/travel/cars";

const cardsBorderRadii ="8px";

export default function AdminCarMaintenance() {
    const toast = useToast();
    const { theme } = useCustomTheme();
    const isDark = theme.name === "dark";

    const [carId, setCarId] = useState("");
    const [isMaintenance, setIsMaintenance] = useState(false);
    const [saving, setSaving] = useState(false);
    const [lastResult, setLastResult] = useState<any>(null);

    const save = async () => {
        if (!carId.trim()) {
            toast({ title: "Car ID is required", status: "warning" });
            return;
        }

        setSaving(true);
        try {
            const res = await axios.post(`${CARS_API}/hire/admin/cars/${carId.trim()}/maintenance`, { isMaintenance });
            if (!res.data?.ok) throw new Error(res.data?.error || "Failed");

            setLastResult(res.data.car);
            toast({ title: "Updated maintenance status", status: "success" });
        } catch (err: any) {
            toast({
                title: "Update failed",
                description: err?.response?.data?.error || err?.message,
                status: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box p={{ base: 4, md: 8 }} color={theme.text}>
            <Heading size="lg" color={theme.text}>
                Admin: Car Maintenance
            </Heading>
            <Text color={theme.textSubtle} mt={1}>
                Mark a car unavailable for maintenance. This prevents new bookings.
            </Text>

            <Box
                mt={6}
                bg={theme.surface}
                border="1px solid"
                borderColor={theme.border}
                borderRadius={cardsBorderRadii}
                p={5}
                maxW="720px"
                boxShadow={theme.shadowSm}
            >
                <VStack align="stretch" spacing={4}>
                    <FormControl>
                        <FormLabel color={theme.textMuted}>Car ID</FormLabel>
                        <Input
                            value={carId}
                            onChange={(e) => setCarId(e.target.value)}
                            placeholder="Paste car _id"
                            bg={theme.inputBg}
                            borderColor={theme.inputBorder}
                            color={theme.text}
                            _placeholder={{ color: theme.inputPlaceholder }}
                            _hover={{ borderColor: theme.inputBorderFocus }}
                            _focus={{
                                borderColor: theme.inputBorderFocus,
                                boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                            }}
                        />
                    </FormControl>

                    <HStack
                        justify="space-between"
                        p={3}
                        borderRadius={cardsBorderRadii}
                        bg={theme.surfaceAlt}
                        border="1px solid"
                        borderColor={theme.border}
                    >
                        <HStack>
                            <Text fontWeight="semibold" color={theme.text}>
                                Maintenance mode
                            </Text>
                            <Badge colorScheme={isMaintenance ? "red" : "green"} borderRadius={cardsBorderRadii} px={2.5}>
                                {isMaintenance ? "ON" : "OFF"}
                            </Badge>
                        </HStack>
                        <Switch isChecked={isMaintenance} onChange={(e) => setIsMaintenance(e.target.checked)} />
                    </HStack>

                    <Divider borderColor={theme.divider} />

                    <HStack justify="flex-end">
                        <Button
                            onClick={save}
                            isLoading={saving}
                            color={theme.textOnPrimary}
                            bgImage={theme.gradients.primaryButton}
                            _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                            borderRadius={cardsBorderRadii}
                            boxShadow={theme.shadowSm}
                        >
                            Save
                        </Button>
                    </HStack>

                    {lastResult && (
                        <Box
                            mt={2}
                            p={3}
                            bg={theme.surfaceAlt}
                            borderRadius={cardsBorderRadii}
                            border="1px solid"
                            borderColor={theme.border}
                        >
                            <Text fontSize="sm" color={theme.textMuted}>
                                Updated car:
                            </Text>
                            <Text fontSize="sm" color={theme.text}>
                                <b>{lastResult.brand || lastResult.make}</b> {lastResult.model} • Maintenance:{" "}
                                <b>{String(lastResult.isMaintenance)}</b>
                            </Text>
                        </Box>
                    )}
                </VStack>
            </Box>
        </Box>
    );
}